import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import {
  Box,
  Text,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Avatar,
  Tab,
  TabPanel,
  VStack,
  Image,
  Spinner,
  Button,
  useColorModeValue,
  StackDivider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody, 
  ModalFooter
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { getAccessToken } from '../utils/auth';
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
} from "@stripe/react-connect-js";
import useStripeConnect from "../hooks/useStripeConnect"; // Custom hook for Stripe Connect
import BookingCard from '../components/BookingCard';
import HandLoader from '../components/HandLoader';

const Bookings = () => {
  const [booked, setBooked] = useState([]);
  const [history, setHistory] = useState([]);
  const [underReview, setUnderReview] = useState([]);
  const [location, setLocation] = useState('');
  const [onboardingChecked, setOnboardingChecked] = useState(false); // Flag to prevent multiple calls
  const [showOnboardingFlow, setShowOnboardingFlow] = useState(false); // Control when to show onboarding
  const onboardingDisclosure = useDisclosure(); // Onboarding modal disclosure
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true); // Loading state for initial data fetching
  const storedAccountId = localStorage.getItem('account_id');
  const [isOpen, setIsOpen] = useState(false);
  // Stripe Connect Instance
  const stripeConnectInstance = useStripeConnect(storedAccountId);
  const navigate = useNavigate();
  const [userType, setUserType] = useState('');
  const bgColor = useColorModeValue('white', 'black');
  const textColor = useColorModeValue('black', 'white');
  const borderColor = useColorModeValue('gray.200', 'white');
  
  useEffect(() => {
    const userTypeFromStorage = localStorage.getItem('user_type');
    setUserType(userTypeFromStorage);

    const fetchBookings = async () => {
      try {
        const backendUrl = process.env.NODE_ENV === 'production'
          ? process.env.REACT_APP_BACKEND_URL
          : 'http://localhost:8080';
        const token = await getAccessToken();
        const endpoint = userTypeFromStorage === 'photographer'
          ? `${backendUrl}/bookings`
          : `${backendUrl}/bookings`;
        
        const response = await axiosInstance.get(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const allBookings = response.data;
        console.log(allBookings)

        // Function to sort bookings by date and time
        const sortBookings = (bookings) => {
          return bookings.sort((a, b) => {
            const dateTimeA = new Date(`${a.from_date}T${a.start_time}`);
            const dateTimeB = new Date(`${b.from_date}T${b.start_time}`);
            return dateTimeA - dateTimeB; // Sort ascending
          });
        };
        
        // Function to format start_time to AM/PM format
        const formatStartTime = (time) => {
          const [hour, minute] = time.split(":").map(Number);
          const period = hour >= 12 ? "PM" : "AM";
          const formattedHour = hour % 12 || 12; // Convert hour to 12-hour format
          return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
        };
        // Function to format from_date
        const formatDate = (dateString) => {
          const date = new Date(dateString);
          // Use toLocaleDateString with options to format the date
          return date.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        };
        
        // Sort bookings and format start_time
        const sortedBookings = sortBookings(allBookings.bookings).map((booking) => ({
          ...booking,
          formatted_start_time: formatStartTime(booking.start_time),
          from_date: formatDate(booking.from_date)
        }));
        
        // Filter and format bookings based on status
        const currentBookings = sortedBookings.filter(
          (booking) =>
            booking.status === "accepted" ||
            booking.status === "in-progress" ||
            booking.status === "pending"
        );
        
        const reviewBookings = sortedBookings.filter(
          (booking) => booking.status === "under-review"
        );
        
        const pastBookings = sortedBookings.filter(
          (booking) => booking.status === "completed" || booking.status === "declined"
        );
        
        // Example to verify output
        console.log("Current Bookings:", currentBookings);
        console.log("Review Bookings:", reviewBookings);
        console.log("Past Bookings:", pastBookings);
        
        setBooked(currentBookings);
        setHistory(pastBookings);
        setUnderReview(reviewBookings);
        setLocation(allBookings.location);
      } catch (err) {
        console.error('Error fetching bookings:', err);
      }
    };
    setLoading(false)
    fetchBookings();
  }, []);

  // Check onboarding status once after the component mounts
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const backendUrl =
          process.env.NODE_ENV === 'production'
            ? process.env.REACT_APP_BACKEND_URL
            : 'http://localhost:8080';
        const response = await axiosInstance.get(`${backendUrl}/onboarding-status`);
        console.log(response)
        if (response.data.status === 'incomplete') {
          onboardingDisclosure.onOpen(); // Trigger the onboarding modal if incomplete
        }
      } catch (error) {
        const backendUrl =
          process.env.NODE_ENV === 'production'
            ? process.env.REACT_APP_BACKEND_URL
            : 'http://localhost:8080';
        const createAccount = await axiosInstance.post(`${backendUrl}/create-connected-account`, {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        const response = await axiosInstance.get(`${backendUrl}/onboarding-status`);
        localStorage.setItem('account_id', response.account.id); 
        onboardingDisclosure.onOpen(); // Trigger the onboarding modal if error
        console.error('Failed to check onboarding status:', error);
      }
    };

    // Check onboarding status only once
    if (!onboardingChecked) {
      checkOnboardingStatus();
      setOnboardingChecked(true); // Prevent future calls
    }
  }, [onboardingChecked, onboardingDisclosure]);


  const handleOnboardingContinue = () => {
    setShowOnboardingFlow(true); // Show the Stripe onboarding flow inside the modal
  };

  const handleBookingClick = (booking) => {
    if (booking && booking.booking_id) {
      navigate(`/booking/${booking.booking_id}`, { state: { booking } });
    }
  };

  if (loading) {
    return (
      <Flex minH="100vh" alignItems="center" justifyContent="center">
        <HandLoader />
      </Flex>
    );
  }
  

  const renderBookingCard = (booking) => {
    const participantSummary = Object.entries(
      booking.items.reduce((acc, item) => {
        acc[item.user_type] = (acc[item.user_type] || 0) + 1;
        return acc;
      }, {})
    ).map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`).join(', ');
  
    const formattedRates = booking.items.map(item =>
      `${item.first_name} - $${item.rate_per_hour}/hr`
    ).join(', ');
  
    return (
      <Box
        key={booking.booking_id}
        bg={bgColor}
        p={4}
        _hover={{
          transform: 'scale(1.03)',
          transition: 'all 0.2s ease-in-out',
        }}
        borderRadius="lg"
        shadow="md"
        mb={4}
        onClick={() => handleBookingClick(booking)}
        cursor="pointer"
      >
        <Flex justify="space-between" align="center">
          <Box>
            <Text fontSize="lg" fontWeight="bold">
              Group Booking
            </Text>
            <Text fontSize="sm" color="gray.500">
              {booking.from_date} @ {booking.formatted_start_time}
            </Text>
            {/*<Text mt={2}>Location: {booking.location || location}</Text>*/}
            <Text mt={1}>{booking.description}</Text>
  
            <Text fontSize="xs" color="gray.500" mt={2}>
              {participantSummary}
            </Text>
  
            <Text fontSize="xs" color="gray.400" mt={1}>
              {formattedRates}
            </Text>
  
            <Text fontSize="sm" color="gray.500" mt={1}>
              Status: {booking.status}
            </Text>
  
            <Text fontSize="sm" color="green.500" fontWeight="bold" mt={1}>
              Total: ${ (booking.total / 100).toFixed(2) }
            </Text>
          </Box>
  
          {/* Avatars */}
          <Flex align="center" gap={2}>
            {booking.items.slice(0, 3).map((item, index) => (
              <a
                key={index}
                href={`/user/${item.user_id}`}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(`/user/${item.user_id}`, '_blank');
                }}
              >
                <Avatar
                  size="md"
                  name={`${item.first_name} ${item.last_name}`}
                  src={item.avatar}
                />
              </a>
            ))}

          </Flex>
        </Flex>
      </Box>
    );
  };
  

  return (
    <Box p={6} bg={bgColor} minH="100vh">
      <Text fontSize="3xl" fontWeight="bold" mb={4}>
        Bookings
      </Text>

      {/* Tabs for Booked, Under Review, and History */}
      <Tabs variant="enclosed" isFitted >
        <TabList mb="1em">
          <Tab>
            <Text color={textColor}>
              Booked
            </Text>
          </Tab>
          <Tab>
            <Text color={textColor}>
              Under Review
            </Text>
          </Tab>
          <Tab>
            <Text color={textColor}>
              History
            </Text>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
          <VStack spacing={4} align="stretch" divider={<StackDivider borderColor={borderColor}/>}>
            {booked.length > 0 ? (
              booked.map((booking) => (
                <BookingCard key={booking.booking_id} booking={booking} onClick={handleBookingClick} />
              ))
              
            ) : (
            <Flex direction="column" align="center" justify="center" minH="60vh">
              <Box mb={2}>
                <HandLoader />
              </Box>
              <Text spacing="" fontSize="md" color="gray.400">Looking for bookings...</Text>
            </Flex>
            )}
          </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch" divider={<StackDivider borderColor={borderColor}/>}>
              {underReview.length > 0 ? (
                underReview.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} onClick={handleBookingClick} />
                ))
              ) : (
                <Flex direction="column" align="center" justify="center" minH="60vh">
                  <Box mb={2}>
                    <HandLoader />
                  </Box>
                  <Text spacing="" fontSize="md" color="gray.400">Looking for bookings...</Text>
              </Flex>  
              )}
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch" divider={<StackDivider borderColor={borderColor}/>}>
              {history.length > 0 ? (
                history.map((booking) => (
                  <BookingCard key={booking.booking_id} booking={booking} onClick={handleBookingClick} />
                ))
              ) : (
                <Flex direction="column" align="center" justify="center" minH="60vh">
                  <Box mb={2}>
                    <HandLoader />
                  </Box>
                <Text spacing="" fontSize="md" color="gray.400">Looking for bookings...</Text>
              </Flex>
  

              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modal for Onboarding */}
      <Modal isOpen={onboardingDisclosure.isOpen} onClose={onboardingDisclosure.onClose} isCentered bg={bgColor}>
        <ModalOverlay />
        <ModalContent bg={bgColor}>
          <ModalCloseButton />
          <ModalBody>
          {showOnboardingFlow ? (
              // Show Stripe Onboarding after "Continue" is clicked
              stripeConnectInstance && (
                <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                  <ConnectAccountOnboarding
                    onExit={() => {
                      console.log("Onboarding exited.");
                      setShowOnboardingFlow(false); // Reset onboarding state after it's completed or exited
                      onboardingDisclosure.onClose(); // Close the modal
                    }}
                  />
                </ConnectComponentsProvider>
              )
            ) : (
              <>
                <Text fontWeight="bold" mb={4} >
                  Onboarding is required to continue accepting payments.
                </Text>
              </>
            )}
          </ModalBody>
          {!showOnboardingFlow && (
            <ModalFooter>
              <Button  
              onClick={handleOnboardingContinue}
              type="submit" 
              bgGradient='linear(to-r, #FF0080, #7928CA)' 
              textColor={'white'}
              _hover={{
                bg: 'green.500',
                bgGradient: 'linear(to-r, red.500, purple.500)'
              }}
              >
                Continue
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Bookings;
